import { CheckPriceBlocker } from "Components/Features/UnexpectedState/";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { MainPath } from "Constants/constants";
import {
  featureToggle,
  isTestOrDevelopmentEnv,
} from "Store/featureToggles/featureToggleUtils";
import { useTexts } from "Store/texts";
import { useABTestingFeature } from "Utils/unleash/flagHooks";
import { FeatureFlagABTest } from "Utils/unleash/flags";
import React, { ReactElement, Suspense, lazy } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { ToggledRoute } from "./ToggledRoute";
import history from "./history";

const HomePage = lazy(() => import("Components/Features/HomePage"));
const CheckPrice = lazy(() => import("Components/Features/CheckPrice"));
const Upsell = lazy(() => import("Components/Features/Upsell"));
const Offer = lazy(() => import("Components/Features/Offer"));
const OfferAccept = lazy(() => import("Components/Features/OfferAccept"));
const Accept = lazy(() => import("Components/Features/Accept"));
const Checkout = lazy(() => import("Components/Features/CheckOut"));
const Receipt = lazy(() => import("Components/Features/Receipt/"));
const UnexpectedState = lazy(
  () => import("Components/Features/UnexpectedState"),
);
const Downtime = lazy(() => import("Components/Features/Downtime"));

const wrapWithSuspense = (element: ReactElement): ReactElement => (
  <Suspense fallback={<ThemedSpinner />}>{element}</Suspense>
);

const NoMatch = (): ReactElement => <Redirect to="/" />;
const renderHomePage = (): ReactElement => wrapWithSuspense(<HomePage />);
const renderCheckPrice = (): ReactElement => wrapWithSuspense(<CheckPrice />);
const renderUpsell = (): ReactElement => wrapWithSuspense(<Upsell />);
const renderOffer = (): ReactElement => wrapWithSuspense(<Offer />);
const renderOfferAccept = (): ReactElement => wrapWithSuspense(<OfferAccept />);
const renderAccept = (): ReactElement => wrapWithSuspense(<Accept />);
const renderCheckout = (): ReactElement => wrapWithSuspense(<Checkout />);
const renderReceipt = (): ReactElement => wrapWithSuspense(<Receipt />);
const renderUnexpectedState = (): ReactElement =>
  wrapWithSuspense(<UnexpectedState />);
const renderDowntime = (): ReactElement => wrapWithSuspense(<Downtime />);

export const AppRoutes = (): ReactElement => {
  const texts = useTexts();
  const isUpsellActive = useABTestingFeature(FeatureFlagABTest.SSX_2858_UPSELL);

  return (
    <Router history={history}>
      <CheckPriceBlocker />
      <Switch>
        <ToggledRoute
          exact
          path="/"
          render={renderHomePage}
          enabled={isTestOrDevelopmentEnv()}
        />
        <ToggledRoute
          render={renderCheckPrice}
          path={`/${MainPath.CHECK_PRICE}/:insuranceType/`}
          enabled={featureToggle(texts.domain.featureToggles.checkPrice)}
        />
        {isUpsellActive && (
          <Route
            exact
            path={`/${MainPath.UPSELL}/:cartId`}
            render={renderUpsell}
          />
        )}
        <Route exact path={`/${MainPath.OFFER}/:cartId`} render={renderOffer} />
        <ProtectedRoute
          exact
          render={renderOfferAccept}
          path={`/${MainPath.OFFER_ACCEPT}/:cartId`}
        />
        <ProtectedRoute
          exact
          render={renderAccept}
          path={`/${MainPath.ACCEPT}/:cartId`}
        />
        <ProtectedRoute
          exact
          path={`/${MainPath.CHECKOUT}/:cartId/:tileId`}
          render={renderCheckout}
        />
        <ProtectedRoute
          exact
          path={`/${MainPath.CHECKOUT}/:cartId/:tileId/:productId`}
          render={renderCheckout}
        />
        <ProtectedRoute
          exact
          path={`/${MainPath.RECEIPT}/:cartId`}
          render={renderReceipt}
        />
        <Route
          exact
          path={`/${MainPath.UNEXPECTED}`}
          render={renderUnexpectedState}
        />
        <Route exact path={`/${MainPath.DOWNTIME}`} render={renderDowntime} />
        <Route component={NoMatch} />
      </Switch>
    </Router>
  );
};
